/**
 * AMD-44 — aerobatics. The turn inertia §3.2 already charges you for, given a body.
 *
 * §3.2 models a hard turn as a real cost: reversal gets its own 128 u/s² decel band
 * against 64 accel and 96 brake, and a 180° reversal from the 8 u/s top speed takes
 * 125 ms to come out the other side. The sim has always billed that. The renderer never
 * showed it — `playerFacing` is `atan2` of velocity, so a 90° cut swept smoothly through
 * the arc and a straight reversal did something worse: velocity crosses zero, falls under
 * `FACING_EPS_SQ`, holds the old heading for a few frames and then pops 180°. The most
 * expensive manoeuvre in the movement model was drawn as a jump cut.
 *
 * This module is the flourish that covers it, and nothing else. It reads sim state, it
 * writes two scale multipliers and one rim offset, and the sim never hears about any of it:
 *
 *  - No sim, balance or collision change. The hit circle is a circle at `player.pos`
 *    with `player.hitbox_radius`, exactly as before.
 *  - No new mesh, no new batch, no new draw call. §14.2 budgets the player at 2 meshes
 *    (body + neon stroke) and it still costs 2 — the flourishes ride the `iScale` the
 *    instanced vertex shader already multiplies local positions by.
 *  - No allocation per frame. All state is scalar fields on one long-lived object.
 *
 * ── Why scale, and why heading survives it ────────────────────────────────────────────
 *
 * `INSTANCED_VERT` does `position.xy * iScale` and *then* rotates by `iRot`, so `iScale.x`
 * runs along the ship's nose axis and `iScale.y` across its span, both in the hull's own
 * frame. The hull is a flat plate at z = 0, so the top-down orthographic image of that
 * plate rotated about one of its own axes is EXACTLY an axis-aligned scale:
 *
 *    pitch θ about the span axis:  (x, y, 0) → (x cos θ, y, −x sin θ)  →  drawn (x cos θ, y)
 *    roll  β about the nose axis:  (x, y, 0) → (x, y cos β,  y sin β)  →  drawn (x, y cos β)
 *
 * So `iScale.x = cos θ` is a LOOP and `iScale.y = cos β` is a ROLL, neither an approximation
 * nor a stylisation, and neither one needs `iRot`.
 *
 * That is the whole answer to the legibility tension. §11 makes the ship the one thing you
 * must never lose, AMD-7 R3 measured it at 1.000 luminance against 0.945–0.972 for
 * everything else, and `tests/player-facing.test.ts` locks a facing bug that shipped. Every
 * flourish here CANNOT move the heading, because the heading is `iRot` and nothing here
 * writes `iRot`. If the tension ever has to be resolved, it has already been resolved in
 * favour of heading, structurally, rather than by tuning.
 *
 * ── Why a loop and not a roll ─────────────────────────────────────────────────────────
 *
 * The first cut of this module drew a roll for both manoeuvres and a playtester threw it
 * out on the physics, correctly: a roll is a rotation about the VELOCITY vector, so it
 * expresses nothing about turning, and a ship rolling through a direction change reads as
 * contradicting the direction change. A loop is a rotation about the SPAN axis, and that
 * is the manoeuvre that reverses you.
 *
 * The same physics fixes the phase, and it is the one place a sign can go wrong. A full
 * 360° loop returns you to the heading you entered on: it is the wrong manoeuvre for a
 * reversal for exactly the reason the roll was. The manoeuvre that reverses you is HALF a
 * loop — over the top, 180° of pitch, out the other way. So the reversal draws θ: π → 2π,
 * a half loop, and the sim's own 180° facing pop supplies the other half.
 *
 * That phase is forced, not chosen. `begin` fires on the frame the integrator sees the
 * pop, so `iRot` has ALREADY flipped by π at t = 0 of the manoeuvre:
 *
 *    t = 0    cos θ = −1   the mirror cancels the pop; the drawn nose still points the way
 *                          the ship came from, continuous with the frame before it
 *    t = 0.5  cos θ =  0   edge-on, floored to LOOP_FLOOR, ~5 px of blade
 *    t = 1    cos θ = +1   level, drawn nose agrees with the heading again, and the
 *                          manoeuvre can end on an untouched hull
 *
 * Putting the mirror at t = 0 is what makes the jump cut disappear: the drawn nose is a
 * continuous function of time through the pop frame, and the only frame where it turns
 * over is the edge-on frame in the middle, where the projection of a nose rotating through
 * vertical has no length and no direction to read. Starting at cos θ = +1 instead would
 * put the mirror in the middle of the manoeuvre and leave the pop naked on frame 0, which
 * is the defect this module exists for.
 *
 * ── Why the bank is continuous and the lateral cut has no event ───────────────────────
 *
 * A jet banks INTO a turn: lift tilts, and sustained turn rate maps to a sustained roll
 * angle. That is a pose, not an event, so `bank` runs every frame off the lateral
 * acceleration the sim is actually applying and there is no trigger, threshold or cooldown
 * anywhere near it. It covers the sharp lateral cut that used to fire the roll, which is
 * why the cut no longer fires anything: a triggered flourish on top of a continuous bank
 * would be a second, weaker statement of the same fact.
 *
 * Two further properties fall out of the same choice:
 *
 *  - Every multiplier is ≤ 1 in magnitude, always. The drawn hull during any flourish is
 *    a strict subset of the footprint it occupies at rest, so AMD-43's 9.4% mean wingtip
 *    overhang can only shrink and never grows. The ship can never appear to lean out of a
 *    hit circle it is not leaning out of.
 *  - It is hull-agnostic. AMD-43 generates one closed outline from 15 parameters; scaling
 *    that outline in its own frame is defined for all 20,000 of them and assumes nothing
 *    about the reference manta's proportions.
 */

import type { RngStream } from '../core/rng';

export const AERO_NONE = 0;
/** Мёртвая петля — a hard reversal. Half a loop over the top, on the span axis. */
export const AERO_LOOP = 2;
/** Knife-edge — a dash. The ship turns onto its side and knifes through. */
export const AERO_KNIFE = 3;

export type AeroKind = typeof AERO_NONE | typeof AERO_LOOP | typeof AERO_KNIFE;

/**
 * Leak time-constant of the turn-energy integrator, in seconds.
 *
 * The window has to be wide enough to hold one whole §3.2 turn and narrow enough that two
 * unrelated turns never sum into a false trigger. A 180° reversal at 128 u/s² from 8 u/s
 * takes 16/128 = 125 ms end to end; a 90° cut runs on the 64 u/s² accel band (the wish
 * vector is perpendicular to velocity, so the dot product is not negative) and also takes
 * 125 ms. 0.20 s covers either with a margin and has forgotten ~92% of a turn 0.5 s later.
 */
export const TURN_TAU = 0.2;

/**
 * Trigger for the loop, in radians of accumulated turn energy.
 *
 * Every number below is MEASURED, by driving the real `World` + `stepPlayer` from a settled
 * 8 u/s and feeding the real `playerFacing` into the real integrator. The reproduction is
 * `tests/aerobatics.test.ts`, which asserts the whole ladder rather than a sample of it.
 *
 *   commanded turn, snapped at 60 Hz     peak energy   fires
 *     30°                                    0.457     nothing
 *     90°                                    1.190     nothing
 *    150°                                    1.672     nothing
 *    170°                                    2.241     nothing
 *    174°                                    2.430     LOOP
 *    180°                                    3.142     LOOP
 *
 *   the same turns, eased instead of snapped
 *    180° over 0.50 s                        1.202     nothing
 *    180° over 1.00 s                        0.650     nothing
 *
 * 2.36 lands the boundary at ~173° of commanded turn, so the loop is reserved for a
 * genuine reversal and a 150° cut cannot reach it. That is deliberate, and it is where the
 * defect this whole module exists for lives: an axial reversal is the ONE input the old
 * renderer drew as a jump cut. Velocity crosses zero, `playerFacing` drops under
 * `FACING_EPS_SQ` and holds the old heading for a few frames, then pops the full 180° in
 * a single frame. Everything short of that turns through an arc the renderer already draws
 * honestly, and gets the continuous bank instead of an event.
 *
 * Frame-rate sensitivity is nil in the safe direction: at 30 Hz the boundary does not move,
 * because the pop is one frame's worth of delta at any rate.
 */
export const LOOP_ENERGY = 2.36;

/** Manoeuvre durations, seconds. */
export const LOOP_DURATION = 0.44;
/** §3.4's dash already owns a 0.30 s trail band; the knife-edge shares it exactly. */
export const KNIFE_DURATION = 0.3;

/**
 * Refractory period after a manoeuvre ENDS, seconds.
 *
 * §3.2 lets you complete a reversal every 125 ms, so a player sawing the stick left-right
 * could ask for eight loops a second. That is nausea, and it is also the failure mode the
 * brief names first. A loop plus this refractory is 0.44 + 0.22 = 0.66 s, capping the ship
 * at 1.5 loops per second, below the ~2 Hz where a repeated whole-body motion stops
 * reading as a series of events and starts reading as a vibration.
 *
 * The dash is exempt: `dash.cooldown_floor_s` is 1.0 s, so the input cannot spam itself,
 * and a dash is the loudest thing the player can ask for — it always gets its flourish.
 */
export const REFRACTORY = 0.22;

/**
 * Floor on |cos θ| at the top of the loop, as a fraction of the drawn hull's length.
 *
 * The 34 px number is the binding one. AMD-43 draws the hull at 1.2× a 0.7 u hitbox, which
 * is 34 px across on a 390 px phone, and a loop that passes through a true zero there is a
 * one-frame disappearance — flicker, not a loop. 0.16 keeps 5.4 px of hull along the
 * heading at the edge-on frame, and `pushNeonStroke` adds its 2 px rim on each side on top
 * of that. Unlike the roll this replaces, the edge-on frame of a loop is the hull's FULL
 * span lying across the heading, so the lit mark is ~34 × 9 px rather than a hairline.
 */
export const LOOP_FLOOR = 0.16;

/** Span at the deepest point of the dash's knife-edge. `acos` of it is 70° of roll. */
export const KNIFE_LAT = 0.34;

/**
 * Hard cap on total roll, radians. `cos(80°)` is 0.174, i.e. 5.9 px of span plus the 4 px
 * the stroke adds. Binds only when a dash knife-edge lands inside an already-deep bank.
 */
export const MAX_ROLL = (80 * Math.PI) / 180;

/**
 * Bank, §3.2's numbers rather than taste.
 *
 * A coordinated turn holds `tan β = a_lat / g`. The ship has no gravity, so its own thrust
 * is the reference: one full accel band of lateral acceleration is one unit of load, and
 * one unit of load banks the ship 45°. `a_lat = v · ω`, which is the right signal rather
 * than ω alone for two reasons. A pirouette at a standstill is not a turn and must not
 * bank. And the reversal that fires the loop has v ≈ 0 at the exact frame `playerFacing`
 * pops, so the pop contributes no bank at all and the loop never has to fight one.
 *
 * What that gives at the speeds §3.2 actually allows: at the 8 u/s cap, ω is clamped at
 * `accel / speed` = 8 rad/s, so `a_lat` reaches one band and no more, and a hard held turn
 * banks 45° — 24 px of span against 34 at rest. BANK_MAX at 60° binds only inside a 32 u/s
 * dash, where the same turn rate carries four times the load; `cos 60° = 0.5` is 17 px plus
 * the stroke's 4. Both measured in `tests/aerobatics.test.ts`.
 */
export const BANK_REF_ACCEL = 64;
export const BANK_MAX = Math.PI / 3;
/** Low-pass on the lateral-acceleration estimate, seconds. Fast enough to read as cause. */
export const BANK_TAU = 0.08;
/**
 * Clamp on the turn rate fed to the bank, rad/s. `player.accel / player.move_speed` is the
 * fastest §3.2 can swing a velocity vector at top speed, so anything above it is not the
 * ship turning: it is `playerFacing` catching up with a velocity that already turned. The
 * one-frame 180° pop reads as 188 rad/s and would otherwise pin the bank on the frame the
 * loop starts.
 */
export const OMEGA_CLAMP = 8;

/**
 * How far the neon rim slides toward the raised wing, as a fraction of the drawn half-span.
 *
 * From directly above, an orthographic bank is symmetric: `cos(+β) = cos(−β)`, so span
 * foreshortening alone says how hard you are turning and says nothing about which way.
 * The asymmetry has to come from somewhere that is not the silhouette of a flat plate.
 *
 * It comes from the hull not being a flat plate. `pushNeonStroke` already draws the rim as
 * a slightly larger copy of the body, which is the hull's outer shell; a shell of depth d
 * banked by β projects its top edge d·sin β further out on the raised side and hides it
 * behind the body on the lowered side. 0.28 is that depth at the centreline as a fraction
 * of the half-span, which is about what a manta-shaped fuselage carries. At the 45° a hard
 * held turn produces it slides the rim 3.4 px at the 34 px drawn size, so the raised wing
 * wears a 5.4 px white edge and the lowered wing has none: one draw call, no new instance
 * channel, no shader change, and it is the projection of a real solid rather than a
 * stylisation.
 *
 * AND IT DOES NOT READ AT 34 px. Measured on a held 45° bank, held velocity, one setting
 * per capture: the luminance above the hull's centreline against the luminance below it
 * splits 0.878 banking left against 0.981 banking right on a 390 px phone, and 0.793
 * against 0.911 at 1920×1080. Real, repeatable, and invisible under the §11 bloom, which
 * blows the ship to 1.000 and smears a 3 px edge into the halo. The bank's MAGNITUDE reads
 * plainly (the span goes 34 px → 24 px and the hull visibly slims); its DIRECTION does not.
 *
 * The alternatives were weighed and cost more than the read is worth. A shear term is not
 * available honestly: `x' = x cos θ + y sin β sin θ` needs a pitch θ, so a PURE bank has no
 * shear in an orthographic projection, and `camera.ts` is an `OrthographicCamera`, so there
 * is no perspective term either — the raised wing is genuinely not closer to the camera.
 * Per-wing brightness is the one that would work, and it needs a signed per-instance
 * channel that every instance in the game would pay for, plus a light direction the art
 * direction does not currently have. The rim stays because it is free, bounded and
 * physically true; it is not load-bearing.
 *
 * The slide is additionally clamped to `1 − cos β`, the room the bank itself just freed, so
 * the rim can never reach further out than the hull does at rest. Below 32° of bank that
 * clamp is what binds, and the rim rides exactly on the resting silhouette's edge.
 */
export const RIM_THICK = 0.28;

/**
 * Loop-rate shaping. `loopPhase(t)` is the fraction of the half loop completed at
 * normalised time t, and it is deliberately NOT linear.
 *
 * A constant-rate loop spends as many frames edge-on as it does level, and edge-on is the
 * expensive place to be at 34 px. This profile has dφ/dt = 1 − 0.7·cos(2πt): 0.3× at the
 * two level poses (t = 0 inverted over the top, t = 1 upright) and 1.7× through the single
 * edge-on frame between them. The ship hangs where it reads and rushes where it does not.
 *
 * The harmonic is 2πt, not the 4πt a full revolution wants: a half loop has ONE edge-on
 * point, at t = 0.5, so the rate has to be slow-fast-slow rather than slow-fast-slow-fast-
 * slow. Endpoints are exact: sin(0) = sin(2π) = 0, so the manoeuvre lands on a whole half
 * revolution and the hull ends level.
 *
 * Measured: the loop spends 13.6% of its time with |cos θ| below 0.35, against the 22.8% a
 * constant-rate loop is stuck with (that band is 41° wide, and geometry gives it 22.8% of
 * any half revolution taken at a constant rate). At LOOP_DURATION that is 3.6 edge-on
 * frames of 26 at 60 Hz, and 1.8 of 13 at the 30 Hz floor.
 */
export function loopPhase(t: number): number {
  return t - (0.7 / (2 * Math.PI)) * Math.sin(2 * Math.PI * t);
}

/** A 0→1→0 hump, fast in and fast out, used for the knife-edge. */
export function hump(t: number): number {
  return Math.sin(Math.PI * t);
}

/** Signed shortest angular difference, wrapped to (−π, π]. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/**
 * One step of the turn-energy integrator: leak what is older than TURN_TAU, add this
 * frame's heading delta.
 *
 * A leak rather than a ring buffer because the renderer's frame delta is not fixed — the
 * bench runs at whatever the machine gives, `updateSlowmo` dilates sim time under bullet
 * time, and a pause hands over dt = 0. `exp(−dt/τ)` is correct for all of them and costs
 * one call. Pure: same inputs, same output, no state, no clock.
 */
export function turnEnergyStep(energy: number, deltaRad: number, dt: number): number {
  const leaked = dt > 0 ? energy * Math.exp(-dt / TURN_TAU) : energy;
  return leaked + Math.abs(deltaRad);
}

/** Which manoeuvre a given turn energy asks for. The whole trigger rule, in one place. */
export function aerobaticForEnergy(energy: number): AeroKind {
  return energy >= LOOP_ENERGY ? AERO_LOOP : AERO_NONE;
}

export function durationFor(kind: AeroKind): number {
  return kind === AERO_KNIFE ? KNIFE_DURATION : LOOP_DURATION;
}

/**
 * Signed bank angle for a signed lateral acceleration, radians. Pure, and the whole bank
 * law: `atan(a_lat / accel)` capped at BANK_MAX.
 */
export function bankAngle(latAccel: number): number {
  const b = Math.atan(latAccel / BANK_REF_ACCEL);
  return b > BANK_MAX ? BANK_MAX : b < -BANK_MAX ? -BANK_MAX : b;
}

/**
 * The scale pair a MANOEUVRE asks for at normalised time t, written into `out`. The bank
 * is not in here; `Aerobatics.update` composes the two.
 *
 * `out[0]` multiplies the nose axis, `out[1]` the span. Exported and pure so the 34 px
 * evidence in `tests/aerobatics.test.ts` can be taken directly off the shipped curve
 * rather than off a copy of it.
 */
export function aerobaticScale(kind: AeroKind, t: number, out: Float32Array): void {
  out[0] = 1;
  out[1] = 1;
  if (kind === AERO_NONE) return;
  const c = t < 0 ? 0 : t > 1 ? 1 : t;

  if (kind === AERO_KNIFE) {
    // No revolution at all: a monotonic squeeze onto the side and back off it. A dash is
    // 0.19 s of displacement and a spin inside it would be the one manoeuvre that really
    // does read as flicker, so the dash gets the pose instead of the rotation.
    out[1] = 1 - (1 - KNIFE_LAT) * hump(c);
    return;
  }

  // θ: π → 2π. Starts mirrored, on the frame `iRot` has just popped by π, so the two
  // cancel and the drawn nose carries on pointing where the ship came from. Ends at +1,
  // level and in agreement with the heading, which is the only place a mirror must never
  // be left. Floored by magnitude so the edge-on frame is a blade and not a gap.
  const lon = Math.cos(Math.PI * (1 + loopPhase(c)));
  out[0] = lon < 0 ? Math.min(lon, -LOOP_FLOOR) : Math.max(lon, LOOP_FLOOR);
}

/**
 * The per-run driver. One instance lives on the renderer for the process lifetime;
 * `reset()` is called from `install`, i.e. once per run.
 */
export class Aerobatics {
  /** Nose-axis scale multiplier for this frame. 1 when nothing is happening. */
  lon = 1;
  /** Span scale multiplier for this frame, bank included. */
  lat = 1;
  /**
   * Signed offset for the neon rim, as a fraction of the drawn half-span, in the hull's
   * own frame: positive slides the rim toward local +y, the ship's left. Zero unless the
   * ship is banking.
   */
  rim = 0;
  /** Signed bank angle in radians, exposed for the dev overlay and the tests. */
  bank = 0;

  private kind: AeroKind = AERO_NONE;
  private elapsed = 0;
  private duration = 0;
  private energy = 0;
  private cooldown = 0;
  private facing = 0;
  private hasFacing = false;
  /** Low-passed lateral acceleration, u/s². The bank is a pure function of it. */
  private latAccel = 0;
  /** Set by `dash()` between frames, consumed by the next `update()`. */
  private dashPending = false;
  private readonly scratch = new Float32Array(2);

  get active(): boolean {
    return this.kind !== AERO_NONE;
  }

  get current(): AeroKind {
    return this.kind;
  }

  /** Turn energy, exposed for the dev overlay and the trigger tests. */
  get turnEnergy(): number {
    return this.energy;
  }

  reset(): void {
    this.lon = 1;
    this.lat = 1;
    this.rim = 0;
    this.bank = 0;
    this.kind = AERO_NONE;
    this.elapsed = 0;
    this.duration = 0;
    this.energy = 0;
    this.cooldown = 0;
    this.facing = 0;
    this.hasFacing = false;
    this.latAccel = 0;
    this.dashPending = false;
  }

  /**
   * §3.4 fired. Queued rather than started here because the bus flush and the frame are
   * different moments, and a manoeuvre must advance on exactly one clock.
   */
  dash(): void {
    this.dashPending = true;
  }

  /**
   * Advance one drawn frame.
   *
   * `facing` is `playerFacing(w)` and `speed` is `|player.vel|` — both pure functions of
   * sim state — and `dt` is the frame's SIM-time delta, so bullet time stretches every
   * flourish with everything else and a pause freezes one mid-loop instead of running it
   * out behind a modal.
   *
   * `vfx` is the presentation stream and the only thing written here. It supplies a ±7%
   * duration jitter per manoeuvre so a player who reverses twice does not get two identical
   * animations. Never a gameplay stream: §7's determinism means a hull, a spawn or a crit
   * must be incapable of noticing that you looped.
   */
  update(facing: number, speed: number, dt: number, vfx: RngStream): void {
    const delta = this.hasFacing ? angleDelta(this.facing, facing) : 0;
    this.facing = facing;
    this.hasFacing = true;

    this.energy = turnEnergyStep(this.energy, delta, dt);
    if (this.cooldown > 0) this.cooldown = Math.max(0, this.cooldown - dt);

    // Bank: low-passed a_lat = v·ω, with ω clamped so a one-frame facing pop contributes
    // one frame of an ordinary hard turn rather than 188 rad/s of nonsense.
    if (dt > 0) {
      let omega = delta / dt;
      if (omega > OMEGA_CLAMP) omega = OMEGA_CLAMP;
      else if (omega < -OMEGA_CLAMP) omega = -OMEGA_CLAMP;
      const k = Math.exp(-dt / BANK_TAU);
      this.latAccel = this.latAccel * k + speed * omega * (1 - k);
    }
    this.bank = bankAngle(this.latAccel);

    if (this.kind !== AERO_NONE) {
      this.elapsed += dt;
      if (this.elapsed >= this.duration) {
        // Not an early return: a dash that lands on this exact frame must still get its
        // knife-edge rather than being held over to the next one.
        this.kind = AERO_NONE;
        this.cooldown = REFRACTORY;
      }
    }

    // A dash outranks everything, including a loop already in flight and the refractory.
    if (this.dashPending) {
      this.dashPending = false;
      this.begin(AERO_KNIFE, vfx);
    } else if (this.kind === AERO_NONE && this.cooldown <= 0) {
      const want = aerobaticForEnergy(this.energy);
      if (want !== AERO_NONE) this.begin(want, vfx);
    }

    this.compose();
  }

  /**
   * Fold the manoeuvre and the bank into the two multipliers the renderer writes.
   *
   * The bank and the knife-edge are rotations about the SAME axis, so they compose by
   * adding angles rather than by multiplying cosines: a dash taken inside a 30° bank is a
   * 100° roll, not a 0.29 span. The loop is a rotation about the other axis and rides on
   * `lon`, so it multiplies through untouched — an aircraft entering a loop out of a banked
   * turn does exactly that, and in practice it barely arises, because `a_lat = v·ω` is
   * near zero at the velocity-crossing that fires a loop in the first place.
   *
   * The one thing this drops is the shear a true composition of the two rotations carries
   * (`x' = x cos θ + y sin β sin θ`), which `iScale` has no term for. It vanishes whenever
   * either angle is zero, so it costs nothing except during a banked loop.
   */
  private compose(): void {
    if (this.kind === AERO_NONE) {
      this.lon = 1;
    } else {
      aerobaticScale(this.kind, this.elapsed / this.duration, this.scratch);
      this.lon = this.scratch[0];
    }
    const pose = this.kind === AERO_KNIFE ? Math.acos(Math.min(1, this.scratch[1])) : 0;
    const bank = this.bank;
    let roll = pose + Math.abs(bank);
    if (roll > MAX_ROLL) roll = MAX_ROLL;
    this.lat = Math.cos(roll);
    // The rim slides toward the raised wing: a left turn (ω > 0) drops the left wing, so
    // the rim goes to local −y. Clamped to the room the bank just freed, which is what
    // keeps the outer edge of the stroke inside its resting footprint.
    const room = 1 - Math.cos(bank);
    const slide = RIM_THICK * Math.abs(Math.sin(bank));
    const side = bank > 0 ? -1 : bank < 0 ? 1 : 0;
    this.rim = side * (slide < room ? slide : room);
  }

  private begin(kind: AeroKind, vfx: RngStream): void {
    this.kind = kind;
    this.elapsed = 0;
    this.duration = durationFor(kind) * (0.93 + 0.14 * vfx.next());
    // The turn has been spent. Without this the same 180° would re-fire the instant the
    // refractory lifted, because the energy has only leaked to 0.66π by then.
    this.energy = 0;
  }
}
